import { Switch, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useGetClientConnections } from 'src/hooks/api/useClientsEndpoint';
import { useUpdateCampaign } from 'src/hooks/api/useSiteEndpoint';
import SettingCard from 'src/pages/Devices/DeviceDetails/TabPages/SensorSettings/components/SettingCard';
import { useAppNavigation } from 'src/routes';

import Credentialing from '@/assets/icons/Credentialing.svg';
import TicketOutlinedCampaign from '@/assets/icons/TicketOutlinedCampaign.svg';
import {
  CarryOutOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  IdcardOutlined,
  LockOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';

import { useSiteDetails } from '../../SiteDetailsContext';
import {
  AddButton,
  ButtonWrapper,
  DetailsWrapper,
  ListItem,
  ListItemContent,
  ListStyled,
  StyledAvatar,
  StyledDescription,
  StyledTitle,
  Wrapper,
} from './Campaigns.styled';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'enroll.wicketsoft.com';
const BASE_URL = `https://${SITE_URL}`;

const ICONS = {
  PointOfSale: TicketOutlinedCampaign,
  Ticketing: TicketOutlinedCampaign,
  RemoteTicketing: CreditCardOutlined,
  AgeVerification: IdcardOutlined,
  AccessControl: LockOutlined,
  Credentialing: Credentialing,
  DayPass: CarryOutOutlined,
  EnrollmentCheckIn: CheckCircleOutlined,
};

type Campaign = {
  id: string;
  name?: string | null;
  pathSegment?: string | null;
  enabled: boolean;
  program?: string;
};

export const Campaigns = () => {
  const { t } = useTranslation();
  const { isDesktop, isMobile } = useGlobal();
  const { site } = useSiteDetails();
  const { mutate: updateCampaign } = useUpdateCampaign();
  const { id } = useParams<{ id: string }>();
  const { navigateTo } = useAppNavigation();
  const { data: connections } = useGetClientConnections();

  const campaignsListData = site?.campaigns?.map((campaign) => ({
    id: campaign.id,
    name: campaign.name,
    pathSegment: campaign.pathSegment,
    enabled: campaign.enabled,
    program: campaign.program,
  })) as Campaign[] | undefined;

  const handleChangeCampaignStatus = (campaignId: string) => {
    const campaign = site?.campaigns?.find((campaign) => campaign.id === campaignId);
    if (id && campaign && campaignId) {
      const payload = {
        id,
        campaignId,
        enabled: !campaign.enabled,
      };
      updateCampaign(payload);
    }
  };

  const handleOnChange = (campaignId: string, e: React.SyntheticEvent) => {
    e.stopPropagation();
    handleChangeCampaignStatus(campaignId);
  };

  const handleClickCampaignUrl = (e: React.MouseEvent<HTMLElement>, pathSegment?: string | null) => {
    e.stopPropagation();
    if (pathSegment) {
      window.open(`${BASE_URL}/${site?.pathSegment}/${pathSegment}`, '_blank');
    }
  };

  return (
    <>
      <SettingCard
        title={t('site.campaigns.title')}
        description={t('site.campaigns.description')}
        data-testid="site-campaigns-card"
        withoutCard
        withoutPadding
        items={[
          {
            content: (
              <Wrapper>
                <ButtonWrapper $isDesktop={isDesktop}>
                  <AddButton onClick={() => navigateTo.campaignNew(id)}>{t('site.campaigns.addCampaign')}</AddButton>
                </ButtonWrapper>
                {site?.campaigns?.length ? (
                  <ListStyled
                    dataSource={campaignsListData}
                    renderItem={(item) => {
                      const campaign = site?.campaigns?.find((campaign) => campaign.id === item.id);
                      let Icon;
                      if (campaign && campaign?.program) {
                        const programKey = String(campaign.program) as keyof typeof ICONS;
                        Icon = ICONS[programKey] || MinusCircleOutlined;
                      } else {
                        Icon = MinusCircleOutlined;
                      }

                      const isCampaignDisabled =
                        !(
                          site?.authenticationConnection?.connectionType === 'Authentication' &&
                          site?.authenticationConnection?.connectionTypeDetail === 'AuthenticationTicketmasterOAuth'
                        ) &&
                        connections?.some(
                          (connection) =>
                            connection.id === campaign?.connectionId &&
                            connection.connectionType === 'OrderFulfillment' &&
                            connection.connectionTypeDetail === 'OrderFulfillmentFortressAiFi',
                        );
                      return (
                        <ListItem
                          $isEnabled={item.enabled}
                          key={item.id}
                          onClick={() => navigateTo.campaign(id, item.id)}
                        >
                          <ListItemContent>
                            <StyledAvatar $program={item?.program} src={<Icon />} data-testid="campaign-avatar" />
                            <DetailsWrapper>
                              <StyledTitle>{item.name}</StyledTitle>
                              <StyledDescription
                                onClick={(e) => handleClickCampaignUrl(e, item.pathSegment)}
                                $pathSegment={item.pathSegment || ''}
                                $isMobile={isMobile}
                              >
                                {item.pathSegment
                                  ? `${SITE_URL}/${site?.pathSegment}/${item.pathSegment}`
                                  : t('site.campaigns.pathNotSet')}
                              </StyledDescription>
                            </DetailsWrapper>
                          </ListItemContent>
                          <Tooltip title={isCampaignDisabled ? t('site.campaigns.campaignDisabledTooltip') : undefined}>
                            <Switch
                              defaultChecked={item.enabled}
                              defaultValue={item.enabled}
                              onChange={(_, e) => handleOnChange(item.id, e)}
                              disabled={isCampaignDisabled}
                            />
                          </Tooltip>
                        </ListItem>
                      );
                    }}
                  />
                ) : null}
              </Wrapper>
            ),
          },
        ]}
      />
    </>
  );
};
